import type { ButtonHTMLAttributes, CSSProperties, ReactNode, Ref } from 'react';
import clsx from 'clsx';
import './LessonNode.css';

/**
 * LessonNode state.
 *
 * - `locked`      Not yet available. Padlock icon. Not tappable. Gray.
 * - `unlocked`    Next lesson the learner can start. Brand green, star icon.
 *                 Pairs well with `breathe` to draw attention.
 * - `in-progress` Some lessons in the skill complete; a partial progress ring
 *                 wraps the bubble (e.g. 2/4 → half-filled ring).
 * - `complete`    All lessons in the skill done. Full ring + check icon.
 * - `golden`      Complete AND perfect-first-try. Gold gradient + crown icon.
 */
export type LessonNodeState = 'locked' | 'unlocked' | 'in-progress' | 'complete' | 'golden';

export interface LessonNodeProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  state: LessonNodeState;
  /** Text shown underneath the bubble (skill name). Keep it short. */
  label?: string;
  /** Diameter in px. Default 72. */
  size?: number;
  /** Total lessons in this skill (for ring + aria-label). Default 4. */
  total?: number;
  /** Completed lessons (for ring + aria-label). Default depends on state. */
  completed?: number;
  /** Optional custom icon; otherwise defaults per state. */
  icon?: ReactNode;
  /** Subtle infinite "breathe" animation — use on the node the learner
   *  should tap next. */
  breathe?: boolean;
  ref?: Ref<HTMLButtonElement>;
}

/**
 * LessonNode — Duolingo-style round skill-tree button.
 */
export function LessonNode({
  state,
  label,
  size = 72,
  total = 4,
  completed,
  icon,
  breathe = false,
  className,
  type,
  onClick,
  ref,
  style,
  ...rest
}: LessonNodeProps) {
  const resolvedCompleted = resolveCompleted(state, total, completed);
  const progress = total > 0 ? Math.max(0, Math.min(1, resolvedCompleted / total)) : 0;

  const isLocked = state === 'locked';
  const resolvedIcon = icon ?? defaultIcon(state);

  // SVG ring math — viewBox 100×100, circle radius 46 leaves room for stroke
  const radius = 46;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - progress);

  const nodeStyle: CSSProperties = {
    ...style,
    ['--fluent-node-size' as string]: `${size}px`,
  };

  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      aria-label={ariaLabelFor(state, label, resolvedCompleted, total)}
      aria-disabled={isLocked || undefined}
      disabled={isLocked || undefined}
      data-state={state}
      data-breathe={breathe || undefined}
      className={clsx('fluent-node', className)}
      style={nodeStyle}
      onClick={isLocked ? undefined : onClick}
      {...rest}
    >
      <span className="fluent-node__bubble">
        <svg className="fluent-node__ring" viewBox="0 0 100 100" aria-hidden="true">
          <circle className="fluent-node__ring-track" cx="50" cy="50" r={radius} />
          <circle
            className="fluent-node__ring-progress"
            cx="50"
            cy="50"
            r={radius}
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
          />
        </svg>
        <span className="fluent-node__icon" aria-hidden="true">
          {resolvedIcon}
        </span>
      </span>
      {label != null && <span className="fluent-node__label">{label}</span>}
    </button>
  );
}

// ----- helpers -----

function resolveCompleted(state: LessonNodeState, total: number, completed: number | undefined): number {
  if (completed != null) return completed;
  switch (state) {
    case 'locked':
    case 'unlocked':
      return 0;
    case 'in-progress':
      return Math.ceil(total / 2); // default mid-way
    case 'complete':
    case 'golden':
      return total;
  }
}

function ariaLabelFor(state: LessonNodeState, label: string | undefined, completed: number, total: number): string {
  const base = label ?? 'გაკვეთილი'; // Georgian for "lesson"
  switch (state) {
    case 'locked':
      return `${base} — ჩაკეტილი`;
    case 'unlocked':
      return `${base} — დაიწყე`;
    case 'in-progress':
      return `${base} — ${completed}/${total}`;
    case 'complete':
      return `${base} — დასრულებული`;
    case 'golden':
      return `${base} — ოქროს`;
  }
}

// Default inline SVG icons per state — kept tiny, stroke-based so they
// recolor cleanly via the surrounding color.

function defaultIcon(state: LessonNodeState): ReactNode {
  switch (state) {
    case 'locked':
      return <LockIcon />;
    case 'unlocked':
    case 'in-progress':
      return <StarIcon />;
    case 'complete':
      return <CheckIcon />;
    case 'golden':
      return <CrownIcon />;
  }
}

function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="10" width="16" height="11" rx="2.5" fill="currentColor" stroke="none" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
    </svg>
  );
}

function StarIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" stroke="none">
      <path d="M12 3l2.72 5.88 6.28.78-4.66 4.39 1.2 6.31L12 17.27 6.46 20.36l1.2-6.31L3 9.66l6.28-.78L12 3z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  );
}

function CrownIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" stroke="none">
      <path d="M3 8l4 4 5-7 5 7 4-4v10H3V8z" />
      <rect x="3" y="18" width="18" height="2.5" rx="0.8" />
    </svg>
  );
}
